import { Link } from 'react-router-dom';
import { Folder, Layers, Video, AudioLines, Image as ImageIcon, FileText, FolderOpen, Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ItemActionMenu } from './ItemActionMenu';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import type { NormalizedItem } from './types';

const RESOURCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  video: Video,
  audio: AudioLines,
  image: ImageIcon,
  doc: FileText,
  folder: FolderOpen,
  project: Layers,
};

interface ItemRowProps {
  item: NormalizedItem;
}

export function ItemRow({ item }: ItemRowProps) {
  const { isSelected, updateIsQuerying, onRefresh } = useItemListContext();
  const { isFavorite } = useItemActions({ updateIsQuerying, onRefresh });
  const selected = isSelected(item.id);
  const favorite = isFavorite(item.id);
  const isProject = item.itemtype === 'project';
  const Icon = isProject ? Layers : Folder;
  const linkTo = `/in/${item.itemtype}/${item.id}`;

  const resourceCounts = getResourceCounts(item.resources);

  return (
    <li>
      <Link to={linkTo} className="block no-underline">
        <div
          className={cn(
            'group flex items-center gap-3 rounded-xl border px-3 py-2 transition-all',
            selected ? 'border-primary bg-primary-muted' : 'border-transparent bg-card hover:bg-secondary hover:shadow-sm',
          )}
        >
          <SelectionCheckbox item={item} />
          <div className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
            isProject ? 'bg-primary-muted' : 'bg-info-light',
          )}>
            <Icon className={cn('h-5 w-5', isProject ? 'text-primary' : 'text-info')} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{item.title}</p>
            <div className="flex items-center gap-2">
              {resourceCounts.length > 0 ? (
                resourceCounts.map(({ key, count, icon: CountIcon }) => (
                  <span key={key} className="flex items-center gap-0.5 text-xs text-muted-foreground">
                    <span>{count}</span>
                    <CountIcon className="h-3 w-3" />
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">
                  {isProject ? 'Empty project' : 'Empty folder'}
                </span>
              )}
            </div>
          </div>
          {favorite && <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />}
          <ItemActionMenu item={item} />
        </div>
      </Link>
    </li>
  );
}

function getResourceCounts(
  resources: unknown,
): Array<{ key: string; count: number; icon: React.ComponentType<{ className?: string }> }> {
  if (!resources || typeof resources !== 'object' || Array.isArray(resources)) return [];
  const counts = resources as Record<string, number | { N?: string }>;
  const result: Array<{ key: string; count: number; icon: React.ComponentType<{ className?: string }> }> = [];
  for (const [key, val] of Object.entries(counts)) {
    const icon = RESOURCE_ICONS[key];
    if (!icon) continue;
    const num = typeof val === 'number' ? val : Number(val?.N || 0);
    if (num > 0) {
      result.push({ key, count: num, icon });
    }
  }
  return result;
}
